<script>
import Layout from '@layouts/adminLayout.vue'
import LoaderIcon from '@components/loader-icon.vue'
import InfoBlock from '@components/info-block.vue'
import MottoIcon from '@components/motto-icon.vue'
import CategoryIconSelector from '@components/category-icon-selector.vue'
import FaqQuestion from '@components/faq-question.vue'
import { getAllFaq } from '@src/api/faq'
import { transformMany as transformFaq } from '@src/api/transformers/faqTransformer'
import _ from 'lodash'

export default {
  page: {
    title: 'Help',
    meta: [
      {
        name: 'description',
        content: 'The Help page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    InfoBlock,
    MottoIcon,
    CategoryIconSelector,
    FaqQuestion,
  },
  data() {
    return {
      loading: true,
      error: false,
      searchQuery: '',
      categories: ['Office Setup', 'Brand', 'Technology', 'Tech Access', 'Compliance', 'Education', 'Industry'],
      activeCategory: null,
      questions: [],
    }
  },

  computed: {
    filteredQuestions() {
      if (this.searchQuery) {
        return this.questions.filter((question) => {
          return question.question.toLowerCase().includes(this.searchQuery.toLowerCase())
        })
      }

      if (this.activeCategory) {
        return this.questions.filter(
          (question) => question.category === this.translateCategoryName(this.activeCategory)
        )
      }

      return this.questions
    },

    isQueryEmpty() {
      return _.isEmpty(this.searchQuery)
    },
  },
  created() {
    this.fetchQuestions()
  },
  methods: {
    async fetchQuestions() {
      try {
        const {
          data: { data: questions },
        } = await getAllFaq()
        this.questions = transformFaq(questions)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
    selectCategory(category) {
      if (this.activeCategory === category) {
        this.activeCategory = null
        return
      }

      this.activeCategory = category
    },

    openTicket() {
      const ticketUrl = 'https://motto.happyfox.com/new/'
      window.open(ticketUrl, '_blank')
    },

    updateSearchQuery: _.debounce(function (newQuery) {
      this.searchQuery = newQuery
    }, 300),

    translateCategoryName(categoryName) {
      const nameDictionary = {
        'Tech Access': 'Technology Access',
      }

      return _.get(nameDictionary, categoryName, categoryName)
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.viewContainer">
      <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
      <div v-else>
        <div class="mb-4">
          <BaseTitle>Help</BaseTitle>
        </div>
        <InfoBlock>
          Unable to find what you're looking for? Submit a ticket below, email help@mottomortgage.com or call
          866-MOTTO60; hours are M-F 8 AM – 6 PM (ET)
        </InfoBlock>
        <div :class="$style.helpSearchContent">
          <h2 class="motto-blue-text text-center d-none d-lg-block">How can we help you?</h2>
          <div class="w-100">
            <div :class="$style.searchContainer">
              <BaseInputText
                name="searchQuery"
                placeholder="Search your question"
                :class="$style.searchBar"
                @update="updateSearchQuery" />
              <MottoIcon :class="$style.searchIcon" name="Search"></MottoIcon>
            </div>
          </div>
          <transition name="slide-y">
            <div v-show="isQueryEmpty">
              <p class="p1 motto-blue-text text-center d-none d-lg-block">Or explore these categories</p>
              <div :class="$style.categoryIconContainer">
                <CategoryIconSelector
                  v-for="(category, index) in categories"
                  :key="`${category}${index}`"
                  :category-name="category"
                  :active="activeCategory === category"
                  @click.native="selectCategory(category)" />
              </div>
            </div>
          </transition>
          <h3 :class="$style.faqTitle">Frequently Asked Questions</h3>
          <div v-if="filteredQuestions.length" :class="$style.questionsContainer">
            <transition-group name="fade">
              <div v-for="(question, index) in filteredQuestions" :key="`${question.question}${index}`">
                <FaqQuestion
                  :question="question.question"
                  :answer="question.answer"
                  :image-url="question.imageUrl"
                  :class="$style.questionBlock" />
              </div>
            </transition-group>
          </div>
          <div v-if="!filteredQuestions.length" class="my-5 text-center">
            <p class="p1 color-dark-gray">No questions were found</p>
          </div>
          <h4 class="text-center color-primary-charcoal font-weight-bold">Still need help?</h4>
          <BaseButton :class="$style.submitButton" theme="lightBlue" @click="openTicket">
            <span>Submit a ticket</span>
          </BaseButton>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.viewContainer {
  @media (max-width: $lg-breakpoint) {
    width: auto;
    min-width: unset;
  }

  width: auto;
}

.helpSearchContent {
  margin-top: 32px;
}

.searchContainer {
  display: flex;
  align-items: center;
  max-width: 647px;
  height: 100%;
  margin: 24px auto 16px auto;
}

.searchBar {
  flex-grow: 1;
  max-width: 100%;
}

.searchIcon {
  margin-left: -40px;
}

.categoryIconContainer {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    width: auto;
    overflow-x: scroll;
  }

  @media (min-width: 1700px) {
    display: flex;
  }

  display: grid;
  width: fit-content;
  margin: 10px auto 0 auto;
  @include grid-center-last(4);

  > div {
    @media (max-width: $lg-breakpoint) {
      margin: 14px 8px 0 8px;
    }

    margin: 14px 16px 0 16px;

    &:first-child {
      @media (max-width: $lg-breakpoint) {
        margin-left: 0;
      }
    }
  }
}

.questionsContainer {
  > span {
    @media (max-width: $lg-breakpoint) {
      grid-template-columns: 1fr;
    }

    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 24px;
    margin-top: 24px;
    margin-bottom: 64px;
  }
}

.questionBlock {
  @media (max-width: $lg-breakpoint) {
    max-width: 80vw;
  }
}

.submitButton {
  display: block;
  width: 193px;
  margin: 18px auto;
}

.faqTitle {
  @media (max-width: $lg-breakpoint) {
    font-size: 16px;
    color: $primary-charcoal;
    text-align: start;
  }

  margin-top: 40px;
  color: $primary-blue;
  text-align: center;
}
</style>
